import React from 'react'
import { useLang } from '../lib/useLang'

function StatusIcon({ status }) {
  if (status === 'success') {
    return (
      <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-accent/15'>
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' className='text-accent'>
          <path d='M20 6L9 17l-5-5' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </div>
    )
  }
  if (status === 'failed') {
    return (
      <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-red-400/10'>
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' className='text-red-600'>
          <path d='M18 6L6 18M6 6l12 12' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </div>
    )
  }
  if (status === 'running') {
    return (
      <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-accent/15'>
        <div className='relative flex h-3 w-3 items-center justify-center'>
          <div className='absolute h-full w-full animate-ping rounded-full bg-accent/15' />
          <div className='h-2 w-2 rounded-full bg-accent/15' />
        </div>
      </div>
    )
  }
  // pending, paused, default
  return (
    <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-ink/[0.03]'>
      <div className='h-1.5 w-1.5 rounded-full bg-ink/[0.03]' />
    </div>
  )
}

export default function FlowGraph({ nodes, edges, currentStep, onSelect, summaries }) {
  const { t } = useLang()
  const order = nodes.slice().sort((a, b) => a.index - b.index)

  return (
    <div className='w-full'>
      <div className='flex flex-col gap-3'>
        {order.map(n => {
          const isActive = currentStep === n.id
          const isSuccess = n.status === 'success'
          const isFailed = n.status === 'failed'
          const isRunning = n.status === 'running'

          return (
            <div
              key={n.id}
              onClick={() => onSelect?.(n.id)}
              className='cursor-pointer rounded-2xl p-4 backdrop-blur-md transition-all hover:scale-[1.01]'
              style={{
                background: isSuccess
                  ? 'rgba(255, 90, 31, 0.04)'
                  : isFailed
                  ? 'rgba(239, 68, 68, 0.04)'
                  : isRunning || isActive
                  ? 'rgba(255, 90, 31, 0.04)'
                  : 'rgba(14, 14, 16, 0.02)',
                border: `1px solid ${
                  isSuccess
                    ? 'rgba(255, 90, 31, 0.12)'
                    : isFailed
                    ? 'rgba(239, 68, 68, 0.12)'
                    : isRunning || isActive
                    ? 'rgba(255, 90, 31, 0.12)'
                    : 'rgba(14, 14, 16, 0.04)'
                }`,
                boxShadow: isActive
                  ? '0 4px 12px rgba(255, 90, 31, 0.1)'
                  : isSuccess
                  ? '0 4px 12px rgba(255, 90, 31, 0.06)'
                  : 'none',
              }}
              title={summaries && summaries[n.id] ? summaries[n.id] : ''}
            >
              <div className='flex items-center gap-4'>
                <StatusIcon status={n.status} />
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className={`text-sm font-medium capitalize ${
                      isSuccess ? 'text-accent' : isFailed ? 'text-red-600/90' : isRunning ? 'text-accent' : 'text-ink'
                    }`}>
                      {n.label}
                    </span>
                    {!!n.attempts && n.attempts > 1 && (
                      <span className='rounded-md bg-ink/[0.03] px-1.5 py-0.5 text-[10px] text-ink'>
                        {t('flowGraph.try')}: {n.attempts}
                      </span>
                    )}
                    {!!n.errors && n.status === 'failed' && (
                      <span className='rounded-md bg-red-500/20 px-1.5 py-0.5 text-[10px] text-red-600'>
                        {n.errors} {n.errors > 1 ? t('flowGraph.errors') : t('flowGraph.error')}
                      </span>
                    )}
                  </div>
                  {summaries && summaries[n.id] && (
                    <p className='mt-1 truncate text-xs text-ink' title={summaries[n.id]}>
                      {summaries[n.id]}
                    </p>
                  )}
                </div>
                <div className='flex items-center'>
                  <span className={`text-[10px] uppercase tracking-wider ${
                    isSuccess ? 'text-accent' : isFailed ? 'text-red-600/60' : isRunning ? 'text-accent' : 'text-ink'
                  }`}>
                    {t(`flowGraph.${n.status}`) || n.status}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
return (
    <div className='w-full'>
      <div className='flex flex-col gap-3'>
        {order.map(n => (
          <div key={n.id}>
            <ModPanel
              bgColor='var(--m-bg-placeholder)'
              padding='var(--x4) var(--x6)'
              className={`w-full cursor-pointer transition-opacity hover:opacity-80 ${statusTextColor(n.status)} ${currentStep === n.id ? 'ring-2 ring-sky-500 ring-offset-2' : ''}`}
              onClick={() => onSelect?.(n.id)}
              title={summaries && summaries[n.id] ? summaries[n.id] : ''}
            >
              <div className='flex w-full flex-col items-center gap-1 text-left'>
                <div className='flex-c'>
                  <span className='rounded-md bg-accent/15 px-1.5 py-0.5 text-xs font-medium capitalize text-accent'>{n.label}</span>
                  {n.status === 'running' && <span className='loading loading-spinner loading-xs' />}
                  {!!n.attempts && <span className='rounded-md bg-ink/[0.06] px-1.5 py-0.5 text-[10px] text-ink/60'>try:{n.attempts}</span>}
                  {!!n.errors && <span className='rounded-md bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600'>{n.errors}</span>}
                </div>
                {summaries && summaries[n.id] && (
                  <div className='w-full truncate text-xs text-ink/50' title={summaries[n.id]}>
                    {summaries[n.id]}
                  </div>
                )}
              </div>
            </ModPanel>
            <div>
              <p>{n.status}</p>

            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
 */
